import React from "react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../lib/api";
import {
    Wrench,
    User2,
    ShieldCheck,
    CalendarCheck2,
    Bike,
    Star,
    ArrowRight,
    Phone,
    MapPin,
} from "lucide-react";

export default function LandingPage() {
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        axios.get(`${API}/public/settings`).then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    const primary =
        settings?.cor_primaria && /^#([0-9A-Fa-f]{6})$/.test(settings.cor_primaria)
            ? settings.cor_primaria
            : "#FF3B30";
    const oficinaNome = settings?.nome || "Oficina Moto";

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="landing-page">
            {/* Top bar */}
            <header className="border-b border-zinc-200 bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-3">
                        {settings?.logo_url ? (
                            <img
                                src={settings.logo_url}
                                alt={oficinaNome}
                                className="h-10 w-10 rounded-sm border border-zinc-200 object-cover"
                            />
                        ) : (
                            <div
                                className="flex h-10 w-10 items-center justify-center rounded-sm text-white"
                                style={{ backgroundColor: primary }}
                            >
                                <Wrench className="h-5 w-5" />
                            </div>
                        )}
                        <div>
                            <div className="font-['Outfit'] text-base font-semibold tracking-tight text-zinc-900">
                                {oficinaNome}
                            </div>
                            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                                Oficina de motas & mobilidade
                            </div>
                        </div>
                    </div>
                    <div className="hidden gap-2 md:flex">
                        <Link
                            to="/avaliacoes"
                            className="rounded-sm px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100"
                        >
                            Opiniões
                        </Link>
                        <Link
                            to="/entrar"
                            className="rounded-sm px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100"
                            data-testid="topbar-cliente-link"
                        >
                            Entrar
                        </Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <main className="mx-auto max-w-6xl px-6 py-12 md:py-16">
                <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">
                    <div>
                        <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-500">
                            Bem-vindo · Marcações online
                        </div>
                        <h1 className="font-['Outfit'] text-4xl font-semibold tracking-tight text-zinc-900 md:text-5xl">
                            A sua mota, pitbike, scooter ou trotinete{" "}
                            <span style={{ color: primary }}>ao cuidado de quem sabe.</span>
                        </h1>
                        <p className="mt-4 max-w-xl text-sm text-zinc-600 md:text-base">
                            Registe-se em segundos, adicione o seu veículo e escolha um horário disponível
                            para trazer a sua moto à oficina — tudo online, sem chamadas.
                        </p>

                        {/* CTA buttons (compactos) */}
                        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                            <Link
                                to="/entrar"
                                data-testid="cta-cliente"
                                className="group inline-flex items-center gap-3 border border-zinc-900 bg-zinc-900 px-5 py-3 text-white transition-all hover:-translate-y-0.5"
                                style={{ backgroundColor: primary, borderColor: primary }}
                            >
                                <User2 className="h-4 w-4" strokeWidth={2} />
                                <span className="text-sm font-semibold">Área do Cliente</span>
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link
                                to="/login"
                                data-testid="cta-admin"
                                className="group inline-flex items-center gap-3 border border-zinc-900 bg-white px-5 py-3 text-zinc-900 transition-all hover:-translate-y-0.5"
                            >
                                <ShieldCheck className="h-4 w-4" strokeWidth={2} />
                                <span className="text-sm font-semibold">Administração</span>
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </div>

                    {/* Hero image */}
                    <div className="relative">
                        <div className="absolute -inset-3 -z-10" style={{ backgroundColor: primary, opacity: 0.08 }} />
                        <img
                            src="https://images.unsplash.com/photo-1558981806-ec527fa84c39?auto=format&fit=crop&w=1200&q=80"
                            alt="Oficina de motos"
                            className="h-72 w-full border border-zinc-200 object-cover md:h-96"
                            data-testid="landing-hero-img"
                        />
                    </div>
                </div>

                {/* Features strip */}
                <section className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Feature
                        icon={Bike}
                        title="Todos os tipos de veículo"
                        desc="Moto, pitbike, scooter, trotinete elétrica — ou o que precisar (categoria 'Outro')."
                    />
                    <Feature
                        icon={CalendarCheck2}
                        title="Marcações online"
                        desc="Escolha entre os horários que a oficina disponibiliza."
                    />
                    <Feature
                        icon={Star}
                        title="Acompanhamento em tempo real"
                        desc="Receba o link único para ver o estado da reparação — sem precisar ligar."
                    />
                </section>

                {/* Contact card */}
                {(settings?.telefone || settings?.morada || settings?.horario) ? (
                    <section className="mt-12 flex flex-col gap-4 border border-zinc-200 bg-white p-6 md:flex-row md:items-center md:justify-between">
                        <div className="flex flex-wrap items-center gap-5 text-sm text-zinc-700">
                            {settings.telefone ? (
                                <div className="flex items-center gap-2">
                                    <Phone className="h-4 w-4 text-zinc-400" />
                                    <span>{settings.telefone}</span>
                                </div>
                            ) : null}
                            {settings.morada ? (
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-zinc-400" />
                                    <span>{settings.morada}</span>
                                </div>
                            ) : null}
                            {settings.horario ? (
                                <div className="whitespace-pre-line text-xs text-zinc-500">
                                    {settings.horario}
                                </div>
                            ) : null}
                        </div>
                    </section>
                ) : null}
            </main>

            <footer className="border-t border-zinc-200 bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 text-xs text-zinc-500">
                    <span>© {new Date().getFullYear()} {oficinaNome}</span>
                    <Link to="/login" className="hover:text-zinc-900">
                        Administração
                    </Link>
                </div>
            </footer>
        </div>
    );
}

function Feature({ icon: Icon, title, desc }) {
    return (
        <div className="border border-zinc-200 bg-white p-5">
            <Icon className="h-5 w-5 text-zinc-700" strokeWidth={1.75} />
            <div className="mt-3 font-['Outfit'] text-base font-semibold text-zinc-900">
                {title}
            </div>
            <p className="mt-1 text-sm text-zinc-600">{desc}</p>
        </div>
    );
}
